/**
 * The kind of player a person is — the single source of truth for navigation
 * (`navigation.ts`, items' `hats`) and the first screen (`home-landing.ts`).
 *
 * Derived from `/me` only: organisation kinds, project parties and roles, the
 * worker record, the entitlement and the staff right. A person may wear several
 * hats (a supplier who also builds a house is `supplier` + `owner`); navigation
 * shows the union and `primary` decides where they land.
 *
 * Hats (one person can hold several):
 *
 * - `supplier`       a member of a supplier company (global; never a project party)
 * - `land_seller`    a member of a land seller company
 * - `owner`          the employer's side: an owner-party member who is not its QS
 *                    (owner, owner's PM, viewer), or a member of an individual
 *                    owner / developer organisation
 * - `developer`      also set for a developer company (it buys land)
 * - `contractor`     a contractor company's member, or a contractor-party member
 * - `subcontractor`  a subcontractor company's member, or a subcontractor-party member
 * - `consultant`     a consultancy's member, or a consultant-party member
 * - `qs_owner_side`  a QS held through the owner party (behind the owner's wall)
 * - `qs_contractor_side`  a QS held through the contractor party
 * - `qs_independent` a QS held through its own QS party, or a QS firm's member
 *                    with no QS project yet
 * - `worker`         a claimed worker record, or no KorOS Build access at all
 *                    (then it is the only hat: nothing else is reachable; a
 *                    company member whose access lapsed still lands home, on the
 *                    "not enabled" explanation)
 * - `provider`       offers work in the marketplace: a listed provider profile
 *                    (`/me.provider`, site work and/or services), or a sole
 *                    trader (own personal organisation with `personal_role`
 *                    "sole_trader") — "My services" (`/me/services`)
 * - `sole_trader`    no KorOS Build access, but a sole trader on projects
 *                    (`/me.entitlements.sole_trader_projects`): works exactly
 *                    those projects (their subcontractor menu, sync, messages)
 *                    and My services; everything else stays "not enabled"
 * - `staff`          KorOS staff (`build.admin`)
 * - `newcomer`       Build access and no other hat: the set-up wizard is for them
 *
 * A **dormant** organisation (`organizations[].is_dormant`: a personal
 * organisation an abandoned set-up left behind — no projects, no other
 * members, nothing refers to it) gives no hat and no navigation section. A
 * **personal** one (`is_personal`, an individual owner's own) gives its hat
 * (`owner`, even before the first project) but is never shown as a company —
 * unless it is a **sole trader's** (`personal_role: "sole_trader"`: a person
 * taking subcontracts under their own name), which gives no owner hat at all.
 */
import type { Me, OrganizationMembership } from '@/lib/api/types';

import { PERMISSIONS } from './permissions';

export type Hat =
  | 'supplier'
  | 'land_seller'
  | 'owner'
  | 'developer'
  | 'contractor'
  | 'subcontractor'
  | 'consultant'
  | 'qs_owner_side'
  | 'qs_contractor_side'
  | 'qs_independent'
  | 'worker'
  | 'provider'
  | 'sole_trader'
  | 'staff'
  | 'newcomer';

/** Hats that work on projects (the personal home, projects, approvals…). */
export const PROJECT_HATS = [
  'owner',
  'contractor',
  'subcontractor',
  'consultant',
  'qs_owner_side',
  'qs_contractor_side',
  'qs_independent',
] as const satisfies readonly Hat[];

export const QS_HATS = [
  'qs_owner_side',
  'qs_contractor_side',
  'qs_independent',
] as const satisfies readonly Hat[];

/** Where the person's home is (see `home-landing.ts`). */
export type PrimaryHome =
  'home' | 'supplier' | 'land_seller' | 'worker' | 'provider' | 'sole_trader' | 'newcomer';

export type PlayerProfile = {
  hats: ReadonlySet<Hat>;
  primary: PrimaryHome;
  /** Organisations that count (every one except dormant personal ones). */
  organizations: readonly OrganizationMembership[];
};

const ORG_KIND_HATS: Record<string, readonly Hat[]> = {
  supplier: ['supplier'],
  land_seller: ['land_seller'],
  individual_owner: ['owner'],
  developer: ['owner', 'developer'],
  contractor: ['contractor'],
  subcontractor: ['subcontractor'],
  consultancy: ['consultant'],
};

const PARTY_HATS: Record<string, Hat> = {
  owner: 'owner',
  contractor: 'contractor',
  subcontractor: 'subcontractor',
  consultant: 'consultant',
};

const QS_SIDE: Record<string, Hat> = {
  owner: 'qs_owner_side',
  contractor: 'qs_contractor_side',
};

/** Organisations that count for this person (dormant personal ones never do). */
export function workingOrganizations(me: Me | null): OrganizationMembership[] {
  return me?.organizations.filter((org) => !org.is_dormant) ?? [];
}

/**
 * The companies the person works through — never their personal organisation
 * (the plumbing an individual owner acts through, `is_personal`), never a
 * dormant one. Everything that presents an organisation as a company (menus,
 * pickers, subtitles) starts here.
 */
export function companyOrganizations(me: Me | null): OrganizationMembership[] {
  return workingOrganizations(me).filter((org) => !org.is_personal);
}

/**
 * The company a project is held through, for a subtitle — nothing for a
 * personal organisation (an individual owner is never shown as a company).
 */
export function projectCompanyName(me: Me | null, organizationId: string): string | undefined {
  const org = me?.organizations.find((entry) => entry.id === organizationId);
  if (org) return org.is_personal ? undefined : org.name;
  const project = me?.projects.find((entry) => entry.organization_id === organizationId);
  return project && !project.organization_is_personal ? project.organization_name : undefined;
}

/** The person's own (non-dormant) personal organisation, if any: their personal pages act through it. */
export function personalOrganization(me: Me | null): OrganizationMembership | undefined {
  return workingOrganizations(me).find((org) => org.is_own_personal);
}

/** The person's own personal organisation is a sole trader's (no owner hat, no company section). */
export function isSoleTraderOrg(org: OrganizationMembership): boolean {
  return Boolean(org.is_own_personal) && org.personal_role === 'sole_trader';
}

/**
 * Offers work in the marketplace: a listed provider profile in either mode, or
 * a sole trader. Identity only — a worker without Build access may be one.
 */
export function isProvider(me: Me): boolean {
  const provider = me.provider;
  if (provider?.listed && (provider.services || provider.site_work)) return true;
  return workingOrganizations(me).some(isSoleTraderOrg);
}

/**
 * Projects the person works without KorOS Build access, as a sole trader (the
 * server's exemption): none with Build access. Only these projects (and the
 * person's own pages) are reachable then.
 */
export function soleTraderProjectIds(me: Me | null): readonly string[] {
  if (!me || me.entitlements.build_access) return [];
  return me.entitlements.sole_trader_projects ?? [];
}

/** May this person open this path without KorOS Build access? (`_app.tsx`) */
export function reachableWithoutBuildAccess(me: Me | null, pathname: string): boolean {
  if (
    ['/me', '/notifications', '/settings', '/support'].some((prefix) => pathname.startsWith(prefix))
  ) {
    return true;
  }
  const projects = soleTraderProjectIds(me);
  if (projects.length === 0) return false;
  if (pathname === '/projects' || pathname === '/projects/') return true;
  if (pathname.startsWith('/sync') || pathname.startsWith('/messages')) return true;
  const match = /^\/projects\/([^/]+)/.exec(pathname);
  return Boolean(match && projects.includes(match[1] ?? ''));
}

export function playerProfile(me: Me | null): PlayerProfile {
  const hats = new Set<Hat>();
  const organizations = workingOrganizations(me);
  if (!me) return { hats, primary: 'home', organizations };
  if (me.permissions.includes(PERMISSIONS.admin)) hats.add('staff');
  if (isProvider(me)) hats.add('provider');

  if (!me.entitlements.build_access) {
    // Without Build access only the worker's own record is reachable. A company
    // member whose access lapsed (and who is no worker) stays home, where the
    // shell explains that Build is not enabled for them.
    // A services-only provider (no worker record) has their own area, not the worker's.
    // A sole trader works their own projects (the server's exemption): those,
    // with the subcontractor's project menu, sync and messages — nothing else.
    const soleTrader = soleTraderProjectIds(me).length > 0;
    if (soleTrader) hats.add('sole_trader');
    const providerOnly = hats.has('provider') && !me.worker;
    if (!providerOnly) hats.add('worker');
    const lapsed = organizations.length > 0 && !me.worker && !providerOnly && !soleTrader;
    return {
      hats,
      primary: soleTrader ? 'sole_trader' : lapsed ? 'home' : providerOnly ? 'provider' : 'worker',
      organizations,
    };
  }

  for (const org of organizations) {
    // A sole trader's own personal organisation is plumbing for subcontracts, never an owner.
    if (isSoleTraderOrg(org)) continue;
    for (const hat of ORG_KIND_HATS[org.kind] ?? []) hats.add(hat);
  }
  for (const project of me.projects) {
    const isQs = project.role === 'quantity_surveyor' || project.party_role === 'quantity_surveyor';
    if (isQs) {
      hats.add(QS_SIDE[project.party_role] ?? 'qs_independent');
    } else {
      const hat = PARTY_HATS[project.party_role];
      if (hat) hats.add(hat);
    }
  }
  const qsFirm = organizations.some((org) => org.kind === 'qs_firm');
  if (qsFirm && !QS_HATS.some((hat) => hats.has(hat))) hats.add('qs_independent');
  if (me.worker) hats.add('worker');

  // Offering services alone is not working on projects: such a person is still
  // a newcomer to Build (the set-up stays theirs), with "My services" besides.
  const working = [...hats].some((hat) => hat !== 'staff' && hat !== 'provider');
  if (!working && !hats.has('staff')) hats.add('newcomer');

  return { hats, primary: primaryHome(hats), organizations };
}

function primaryHome(hats: ReadonlySet<Hat>): PrimaryHome {
  if (PROJECT_HATS.some((hat) => hats.has(hat))) return 'home';
  if (hats.has('supplier')) return 'supplier';
  if (hats.has('land_seller')) return 'land_seller';
  if (hats.has('worker')) return 'worker';
  if (hats.has('provider')) return 'provider';
  if (hats.has('newcomer')) return 'newcomer';
  return 'home'; // KorOS staff
}

export function hasHat(profile: PlayerProfile, ...hats: readonly Hat[]): boolean {
  return hats.some((hat) => profile.hats.has(hat));
}
